<script>
import {PerfectScrollbar} from 'vue3-perfect-scrollbar';
import AkTitleBackgroundText from '@components/v2/general/AkTitleBackgroundText.vue';
import AkButtonValidationV2 from '@components/v2/general/AkButtonValidationV2.vue';
import RatingStar from '@views/revenuemanagement/RatingStar.vue';
import reviewService from '@services/reviewService';
import GaFilter from '@components/v2/filter/GaFilter.vue';
import InputText from 'primevue/inputtext';
import {debounce} from '@utils';
import {defineAsyncComponent} from 'vue';
import AkPictureAccommodation from '@components/v2/general/AkPictureAccommodation.vue';
import AiReplyDialog from '@views/reviews/aiReplyDialog.vue';
import ReviewListItem from '@views/reviews/parts/ReviewListItem.vue';
import AkShimmer from '@components/v2/general/AkShimmer.vue';
import ReviewStats from '@views/reviews/parts/ReviewStats.vue';
import PermissionsMixinComputed from '@mixins/permissionsMixinComputed';
import OwnerAutocompleteMixin from '@mixins/autocomplete/ownerAutocompleteMixin';
import AccommodationAutocompleteMixin from '@mixins/autocomplete/accommodationAutocompleteMixin';
import ClientAutocompleteMixin from '@mixins/autocomplete/clientAutocompleteMixin';
import GaReviewMobileDetail from '@views/reviews/GaReviewMobileDetail.vue';

/*
 * Filters
 * */

const GaFilterAccommodation = defineAsyncComponent(
  () => import('@components/v2/filter/sidebarFilters/GaFilterAccommodation.vue'),
);
const GaFilterChargeDeCompte = defineAsyncComponent(
  () => import('@components/v2/filter/sidebarFilters/GaFilterChargeDeCompte.vue'),
);
const GaFilterHouseKeepers = defineAsyncComponent(
  () => import('@components/v2/filter/sidebarFilters/GaFilterHouseKeepers.vue'),
);
const GaFilterReservationSite = defineAsyncComponent(
  () => import('@components/v2/filter/sidebarFilters/GaFilterReservationSite.vue'),
);
const GaFilterOwner = defineAsyncComponent(() => import('@components/v2/filter/sidebarFilters/GaFilterOwner.vue'));
const GaFilterClient = defineAsyncComponent(() => import('@components/v2/filter/sidebarFilters/GaFilterClient.vue'));
const GaFilterReservation = defineAsyncComponent(
  () => import('@components/v2/filter/sidebarFilters/GaFilterReservation.vue'),
);
const GaFilterRating = defineAsyncComponent(() => import('@components/v2/filter/sidebarFilters/GaFilterRating.vue'));
const GaFilterCalendarRange = defineAsyncComponent(
  () => import('@components/v2/filter/sidebarFilters/GaFilterCalendarRange.vue'),
);
const GaFilterOffer = defineAsyncComponent(() => import('@components/v2/filter/sidebarFilters/GaFilterOffer.vue'));
const GaFilterTags = defineAsyncComponent(() => import('@components/v2/filter/sidebarFilters/GaFilterTags.vue'));

export default {
  name: 'ReviewList.vue',
  components: {
    GaReviewMobileDetail,
    ReviewStats,
    ReviewListItem,
    AkShimmer,
    AiReplyDialog,
    AkPictureAccommodation,
    InputText,
    GaFilter,
    RatingStar,
    AkButtonValidationV2,
    AkTitleBackgroundText,
    PerfectScrollbar,
  },
  head() {
    return {
      title: 'review.title',
    };
  },
  mixins: [PermissionsMixinComputed, OwnerAutocompleteMixin, AccommodationAutocompleteMixin, ClientAutocompleteMixin],
  data() {
    return {
      monofilter: null,
      showSearch: !this.$isMobile(),
      debounceFn: null,
      loading: false,
      loadingItemsCount: false,
      loadingMore: false,
      filter: {
        page: 1,
        query: '',
        limit: 20,
        sort: ['submitted_at:DESC'],

        ownerIds: null,
        accommodationIds: null,
        mandatOffers: null,
        tagIds: null,
        clientIds: null,
        reservationIds: null,

        housekeeperIds: null,
        chargeDeCompteIds: null,
        ratingRange: null,
        checkinPeriod: [],
        checkoutPeriod: [],
        creationDatePeriod: [],
        submittedDatePeriod: [],
        reservationSites: null,
        reviewer: 'guest',
        status: 'all', // 'answered', 'unanswered'
      },
      nbItems: null,
      total: null,
      nbReplied: null,
      list: [null, null, null, null],
    };
  },
  mounted() {
    this.debounceFn = debounce(this.search, 500);
    this.search(true, false);

    if (this.$route.params.reviewId && this.$refs.detailsPanel) {
      this.$refs.detailsPanel.scrollIntoView();
    }

    this.$nextTick(() => {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !this.loading && !this.loadingMore && this.hasMore) {
            this.loadMore();
          }
        },
        {root: this.$refs.reviewList, threshold: 0.1},
      );
      if (this.$refs.sentinel) this.observer.observe(this.$refs.sentinel);
    });
  },
  beforeUnmount() {
    if (this.observer) this.observer.disconnect();
  },
  methods: {
    mobileShowSearch() {
      if (!this.$isMobile()) return;
      this.showSearch = true;
      this.$nextTick(() => {
        this.$refs.searchBar.$el.focus();
      });
    },
    mobileHideSearch() {
      if (!this.$isMobile()) return;
      this.showSearch = false;
    },
    clearSearch() {
      const prevQuery = this.filter.query;
      this.filter.query = null;
      this.mobileHideSearch();
      if (prevQuery && prevQuery.length) {
        this.search();
      }
    },
    setListFilter(name) {
      this.filter.status = name;
      this.search(false);
    },
    applyFilter() {
      this.$refs.stats.search();
      this.search();
    },
    search(updateItemsCount = true, forceRefresh = true) {
      this.filter.page = 1;
      this.loading = true;
      this.loadingItemsCount = updateItemsCount;
      this.list = [null, null, null, null];
      reviewService
        .reviewsPagination(this.filter)
        .then((data) => {
          this.list = data.list;
          this.nbItems = data.paginationTotal;

          if (updateItemsCount) {
            this.total = data.total;
            this.nbReplied = data.nbReplied;
          }

          if (forceRefresh && this.activeReviewId && !this.list.some((item) => item.id === this.activeReviewId)) {
            this.$router.push({name: 'reviews'});
          }
        })
        .finally(() => {
          this.loading = false;
          this.loadingItemsCount = false;
        });
    },
    loadMore() {
      if (this.loading || this.loadingMore || !this.hasMore) return;
      this.filter.page += 1;
      this.loadingMore = true;
      reviewService
        .reviewsPagination(this.filter)
        .then((data) => {
          this.list = [...this.list, ...data.list];
        })
        .finally(() => (this.loadingMore = false));
    },
    openAiReplyDialog(review) {
      this.$refs.aiReplyDialog.show(review);
    },
    autoFillDetailReply(reply) {
      this.$refs.detail.setReply(reply);
    },
    replyActiveReview() {
      const target = this.list.find((r) => r.id === this.activeReviewId);
      if (target) target.dateReplied = new Date();
      this.nbReplied++;
    },
    showAccommodationFilter() {
      this.monofilter = 'accommodation';
      this.$nextTick(() => this.$refs.filters.showSidebar());
    },
    showOwnerFilter() {
      this.monofilter = 'owner';
      this.$nextTick(() => this.$refs.filters.showSidebar());
    },
    showClientFilter() {
      this.monofilter = 'client';
      this.$nextTick(() => this.$refs.filters.showSidebar());
    },
    resetMonoFilter() {
      this.monofilter = null;
    },
    resetFilter() {
      this.filter = {
        page: 1,
        query: '',
        limit: 20,
        sort: ['submitted_at:DESC'],
        reviewer: 'guest',
        ownerIds: null,
        accommodationIds: null,
        mandatOffers: null,
        tagIds: null,
        clientIds: null,
        reservationIds: null,
        housekeeperIds: null,
        chargeDeCompteIds: null,
        ratingRange: null,
        checkinPeriod: [],
        checkoutPeriod: [],
        creationDatePeriod: [],
        submittedDatePeriod: [],
        reservationSites: null,
        status: 'all',
      };
      this.applyFilter();
    },
    closeMobileDetail() {
      this.$router.push({name: 'reviews'});
    },
  },
  computed: {
    activeReviewId() {
      return Number(this.$route.params.reviewId);
    },
    searchVisible() {
      return this.showSearch || (this.filter.query && this.filter.query.length);
    },
    filterComponents() {
      if (this.monofilter === 'accommodation') {
        return [
          {
            component: GaFilterAccommodation,
            filterProperty: 'accommodationIds',
            showIcon: true,
            canGoBack: false,
            hideFromCount: true,
            label: 'Logement',
          },
        ];
      } else if (this.monofilter === 'owner') {
        return [
          {
            component: GaFilterOwner,
            filterProperty: 'ownerIds',
            showIcon: true,
            canGoBack: false,
            hideFromCount: true,
            label: 'Hôte',
          },
        ];
      } else if (this.monofilter === 'client') {
        return [
          {
            component: GaFilterClient,
            filterProperty: 'clientIds',
            showIcon: true,
            canGoBack: false,
            hideFromCount: true,
            label: 'Voyageur',
          },
        ];
      }

      const components = [
        {
          component: GaFilterAccommodation,
          filterProperty: 'accommodationIds',
          showIcon: true,
          label: 'Logement',
          hideFromCount: !this.$isMobile(),
        },
        {
          component: GaFilterOffer,
          filterProperty: 'mandatOffers',
          showIcon: true,
          label: 'Formule',
        },
        {
          component: GaFilterClient,
          filterProperty: 'clientIds',
          showIcon: true,
          label: 'Voyageur',
          hideFromCount: !this.$isMobile(),
        },
        {
          component: GaFilterReservation,
          filterProperty: 'reservationIds',
          showIcon: true,
          label: 'Réservation',
        },
        {
          component: GaFilterRating,
          filterProperty: 'ratingRange',
          showIcon: true,
          canGoBack: false,
          label: 'Note',
        },
        {
          component: GaFilterCalendarRange,
          filterProperty: 'checkinPeriod',
          showIcon: true,
          canGoBack: false,
          label: 'Date de check in',
        },
        {
          component: GaFilterCalendarRange,
          filterProperty: 'checkoutPeriod',
          showIcon: true,
          canGoBack: false,
          label: 'Date de check out',
        },
        {
          component: GaFilterCalendarRange,
          filterProperty: 'creationDatePeriod',
          showIcon: true,
          canGoBack: false,
          label: 'Date de création',
        },
        {
          component: GaFilterCalendarRange,
          filterProperty: 'submittedDatePeriod',
          showIcon: true,
          canGoBack: false,
          label: 'Date de dépôt',
          hideFromCount: true,
        },
        {
          component: GaFilterReservationSite,
          filterProperty: 'reservationSites',
          showIcon: true,
          label: 'Plateforme',
        },
      ];
      if (this.isGasGav) {
        components.splice(0, 0, {
          component: GaFilterOwner,
          filterProperty: 'ownerIds',
          showIcon: true,
          label: 'Hôte',
          hideFromCount: !this.$isMobile(),
        });

        components.splice(3, 0, {
          component: GaFilterTags,
          filterProperty: 'tagIds',
          showIcon: true,
          label: 'Étiquettes',
        });

        components.splice(
          6,
          0,
          {
            component: GaFilterHouseKeepers,
            filterProperty: 'housekeeperIds',
            showIcon: true,
            label: 'Concierge',
          },
          {
            component: GaFilterChargeDeCompte,
            filterProperty: 'chargeDeCompteIds',
            showIcon: true,
            label: 'Chargé de compte',
          },
        );
      }
      return components;
    },
    isListAll() {
      return this.filter.status === 'all';
    },
    isListAnswered() {
      return this.filter.status === 'answered';
    },
    isListUnanswered() {
      return this.filter.status === 'unanswered';
    },
    unansweredCount() {
      return this.total - this.nbReplied;
    },
    hasMore() {
      if (this.nbItems === null) return false;
      return this.list.filter(Boolean).length < this.nbItems;
    },
    hasActiveFilters() {
      const f = this.filter;
      return !!(
        f.query ||
        f.ownerIds?.length ||
        f.accommodationIds?.length ||
        f.mandatOffers?.length ||
        f.tagIds?.length ||
        f.clientIds?.length ||
        f.reservationIds?.length ||
        f.housekeeperIds?.length ||
        f.chargeDeCompteIds?.length ||
        f.ratingRange?.length ||
        f.checkinPeriod?.length ||
        f.checkoutPeriod?.length ||
        f.creationDatePeriod?.length ||
        f.submittedDatePeriod?.length ||
        f.reservationSites?.length ||
        f.status !== 'all'
      );
    },
  },
};
</script>

<template>
  <ReviewStats ref="stats" :filter="filter" />
  <div
    class="flex flex-col md:pt-8 pt-6 md:px-8 px-4 -mx-5 -mb-[80px] [&_#filterButton]:self-center [&_#filterSlot]:items-center [&_#filterSlot]:gap-2">
    <GaFilter
      ref="filters"
      class="w-full"
      :filter-components="filterComponents"
      v-model:filter="filter"
      :showCadre="false"
      :show-active-filter="!$isMobile()"
      @apply="applyFilter"
      @close="resetMonoFilter"
      @reset="resetFilter">
      <template #inlineFilters="{internalFilter}">
        <div
          @click="mobileShowSearch"
          class="flex gap-1 h-[56px] items-center sm:pr-6 border border-grey-100 rounded-full md:w-[488px] transition-all duration-200 overflow-hidden"
          :class="searchVisible ? 'pl-3 w-full' : 'px-[15px]'">
          <i class="ga-icon ga-search text-3xl text-grey-900 shrink-0" />
          <Transition name="search-input">
            <InputText
              v-if="searchVisible"
              ref="searchBar"
              placeholder="Logement, voyageur, hôte, n° de réservation..."
              v-model="filter.query"
              class="h-full input-class rounded-full px-2 w-full min-w-0 placeholder-shown:text-ellipsis"
              @blur="mobileHideSearch"
              @keydown.enter="$refs.searchBar.$el.blur()"
              @keyup="debounceFn" />
          </Transition>
          <button
            v-if="searchVisible && $isMobile()"
            @mousedown.prevent
            @click.stop="clearSearch"
            class="shrink-0 pr-3">
            <i class="ga-icon ga-supprimer text-xl text-grey-400" />
          </button>
        </div>
        <template v-if="!$isMobile()">
          <div
            v-if="isGasGav"
            class="w-fit h-[46px] px-4 gap-1 cursor-pointer text-grey-900 flex items-center justify-between border border-grey-100 hover:bg-grey-50 rounded-md"
            @click="showOwnerFilter">
            <span class="font-medium">Hôte</span>

            <div
              v-if="internalFilter.ownerIds.value?.length"
              class="rounded-full -mr-1 font-semibold text-sm text-white bg-primary flex items-center justify-center w-5 h-5 select-none">
              {{ internalFilter.ownerIds.value.length }}
            </div>
          </div>
          <div
            class="w-fit h-[46px] px-4 gap-1 cursor-pointer text-grey-900 flex items-center justify-between border border-grey-100 hover:bg-grey-50 rounded-md"
            @click="showAccommodationFilter">
            <span class="font-medium">Logement</span>
            <div
              v-if="internalFilter.accommodationIds.value?.length"
              class="rounded-full -mr-1 font-semibold text-sm text-white bg-primary flex items-center justify-center w-5 h-5 select-none">
              {{ internalFilter.accommodationIds.value.length }}
            </div>
          </div>
          <div
            class="w-fit h-[46px] px-4 gap-1 cursor-pointer text-grey-900 flex items-center justify-between border border-grey-100 hover:bg-grey-50 rounded-md"
            @click="showClientFilter">
            <span class="font-medium">Voyageur</span>
            <div
              v-if="internalFilter.clientIds.value?.length"
              class="rounded-full -mr-1 font-semibold text-sm text-white bg-primary flex items-center justify-center w-5 h-5 select-none">
              {{ internalFilter.clientIds.value.length }}
            </div>
          </div>
        </template>
      </template>
    </GaFilter>
    <div class="flex gap-8 pt-8" ref="detailsPanel">
      <div
        class="flex flex-col md:max-h-[calc(100vh_-_80px_-_104px)] max-h-[calc(100vh_-_32px)] pb-4 md:pb-0 md:sticky top-[calc(80px_+_52px)] gap-6 md:w-1/3 md:min-w-[500px] w-full shrink-0">
        <div v-if="isGasGav" class="flex gap-2 shrink-0 h-fit overflow-x-auto -mx-4 px-4 no-scrollbar">
          <div
            @click="setListFilter('all')"
            class="cursor-pointer select-none bg-grey-50 rounded-full flex gap-2 h-12 items-center py-2 px-4 border border-transparent"
            :class="{'!border-secondary bg-secondary-50': isListAll}">
            <span class="text-grey-900 text-nowrap font-medium">Tous</span>
          </div>
          <div
            @click="setListFilter('unanswered')"
            class="cursor-pointer select-none bg-grey-50 rounded-full flex gap-2 h-12 items-center py-2 px-4 border border-transparent"
            :class="{'!border-secondary bg-secondary-50': isListUnanswered}">
            <span class="text-grey-900 text-nowrap font-medium">En attente de réponse</span>
            <AkShimmer :loading="loadingItemsCount" height="23.5px" width="32px" border-radius="9999px" color="#fb923c">
              <span class="px-2.5 py-0.5 bg-orange-400 text-white text-[13px] font-medium rounded-full">
                {{ unansweredCount }}
              </span>
            </AkShimmer>
          </div>
          <div
            @click="setListFilter('answered')"
            class="cursor-pointer select-none bg-grey-50 rounded-full flex gap-2 h-12 items-center py-2 px-4 border border-transparent"
            :class="{'!border-secondary bg-secondary-50': isListAnswered}">
            <span class="text-grey-900 text-nowrap font-medium">Répondus</span>
            <AkShimmer :loading="loadingItemsCount" height="23.5px" width="32px" border-radius="9999px" color="#22c55e">
              <span class="px-2.5 py-0.5 bg-green-500 text-white text-[13px] font-medium rounded-full">
                {{ nbReplied }}
              </span>
            </AkShimmer>
          </div>
        </div>
        <span class="text-lg font-semibold text-grey-900">
          <AkShimmer :loading="loading" height="27px" width="80px">{{ nbItems }} avis</AkShimmer>
        </span>
        <div ref="reviewList" class="rounded-xl border border-grey-100 divide-y divide-grey-100 overflow-y-auto">
          <!-- REVIEW LIST       -->
          <div v-for="item in list" :key="item?.id">
            <router-link
              :to="
                item && activeReviewId !== item.id
                  ? {name: 'review-detail', params: {reviewId: item.id}}
                  : {name: 'reviews'}
              ">
              <review-list-item
                :class="{'bg-secondary-50 !border-secondary': activeReviewId === item?.id}"
                :review="item" />
            </router-link>
          </div>
          <div
            v-if="!loading && nbItems === 0 && hasActiveFilters"
            class="flex flex-col items-center justify-center py-12 gap-2 text-center px-6">
            <i class="ga-icon ga-search text-3xl text-grey-300" />
            <span class="text-grey-500 font-medium text-sm">
              Aucun résultat correspondant aux critères sélectionnés
            </span>
          </div>
          <review-list-item v-if="loadingMore" :review="null" />
          <div ref="sentinel" class="!border-0" />
        </div>
      </div>
      <div v-if="!$isMobile()" class="grow h-full">
        <router-view :key="$route.params.reviewId" v-slot="{Component}">
          <component :is="Component" @showAiReply="openAiReplyDialog" @replied="replyActiveReview" ref="detail" />
        </router-view>
        <div
          v-if="!activeReviewId"
          class="flex items-center flex-col h-[calc(100vh_-_80px_-_52px)] w-full justify-center">
          <span class="text-grey-500 font-medium">Aucun avis sélectionné.</span>
          <span class="text-grey-400 text-sm">Sélectionnez un avis pour le voir en détail</span>
        </div>
      </div>
      <GaReviewMobileDetail v-else :visible="!!activeReviewId" @hide="closeMobileDetail">
        <router-view :key="$route.params.reviewId" v-slot="{Component}">
          <component :is="Component" @showAiReply="openAiReplyDialog" @replied="replyActiveReview" ref="detail" />
        </router-view>
      </GaReviewMobileDetail>
    </div>
  </div>
  <ai-reply-dialog ref="aiReplyDialog" @selectReply="autoFillDetailReply" />
</template>

<style scoped>
:deep(.input-class) {
  &:hover,
  &:focus {
    @apply !bg-transparent;
  }
}

.search-input-enter-active {
  transition: opacity 0.15s ease 0.05s;
}

.search-input-leave-active {
  transition: opacity 0.1s ease;
}

.search-input-enter-from,
.search-input-leave-to {
  opacity: 0;
}

:deep(#filterSlot) {
  @apply flex-wrap md:flex-nowrap;
}

:deep(#filterSlot .p-dropdown.form-control) {
  padding: 13px 16px;
}
</style>
